import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SearchPanel } from "./components/SearchPanel";
import { PageWidth, ScorePill, StarRow } from "./components/travel-ui";
import { useMarketplaceSearch } from "./context/SearchContext";
import { MOCK_HOTELS, nightsBetween } from "./data/mock-hotels";
import { formatMoney } from "./lib/format";
import { quoteStay } from "./lib/pricing";
import { useWallet } from "./services/wallet.service";

type SortKey = "recommended" | "price" | "rating";

export default function MemberHotels() {
  const navigate = useNavigate();
  const { search, setSearch } = useMarketplaceSearch();
  const wallet = useWallet();

  useEffect(() => {
    setSearch({ product: "stays" });
    // Mount-only: keep the search bar on stays while this page is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [freeCancel, setFreeCancel] = useState(false);
  const [breakfast, setBreakfast] = useState(false);
  const [stars, setStars] = useState<number | null>(null);
  const [sort, setSort] = useState<SortKey>("recommended");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const nights = Math.max(1, nightsBetween(search.checkIn, search.checkOut));
  const query = search.destination.trim().toLowerCase();

  const rows = useMemo(() => {
    const matched = MOCK_HOTELS.filter((hotel) => {
      const text = `${hotel.city} ${hotel.name} ${hotel.state}`.toLowerCase();
      if (query && !text.includes(query)) return false;
      if (freeCancel && !hotel.freeCancellation) return false;
      if (breakfast && !hotel.breakfastIncluded) return false;
      if (stars && hotel.stars < stars) return false;
      return true;
    }).map((hotel) => ({
      hotel,
      quote: quoteStay(hotel.rooms[0], search.checkIn, search.checkOut, search.rooms),
    }));
    matched.sort((a, b) => {
      if (sort === "price") return (a.quote?.total ?? 0) - (b.quote?.total ?? 0);
      if (sort === "rating") return b.hotel.rating - a.hotel.rating;
      return 0;
    });
    return matched;
  }, [
    query,
    freeCancel,
    breakfast,
    stars,
    sort,
    search.checkIn,
    search.checkOut,
    search.rooms,
  ]);

  return (
    <div>
      <div className="border-b border-[#e7e7e7] bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <SearchPanel
            onSearch={(product) => {
              setSearch({ product });
              navigate(product === "stays" ? "/member/hotels" : "/member/flights");
            }}
          />
        </div>
      </div>
      <PageWidth>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">
              {search.destination.trim() || "All destinations"}
            </h1>
            <p className="text-sm text-[#595959]">
              {rows.length} propert{rows.length === 1 ? "y" : "ies"} · {nights} night
              {nights === 1 ? "" : "s"} · {search.guests} adult
              {search.guests === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-[#e7e7e7] bg-white px-3 py-2 text-sm lg:hidden"
              onClick={() => setFiltersOpen((open) => !open)}
            >
              Filters
            </button>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              className="rounded-md border border-[#e7e7e7] bg-white px-3 py-2 text-sm"
              aria-label="Sort stays"
            >
              <option value="recommended">Recommended</option>
              <option value="price">Price (lowest)</option>
              <option value="rating">Guest rating</option>
            </select>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
          <aside className={filtersOpen ? "block" : "hidden lg:block"}>
            <div className="space-y-4 rounded-lg border border-[#e7e7e7] bg-white p-4 text-sm">
              <p className="font-bold">Filter</p>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={freeCancel}
                  onChange={(event) => setFreeCancel(event.target.checked)}
                />
                Free cancellation
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={breakfast}
                  onChange={(event) => setBreakfast(event.target.checked)}
                />
                Breakfast included
              </label>
              <div>
                <p className="mb-2 font-semibold">Star rating</p>
                {[5, 4].map((value) => (
                  <label key={value} className="mb-1 flex items-center gap-2">
                    <input
                      type="radio"
                      name="stars"
                      checked={stars === value}
                      onChange={() => setStars(value)}
                    />
                    {value} stars
                  </label>
                ))}
                <button
                  type="button"
                  className="mt-2 text-[#006ce4]"
                  onClick={() => {
                    setStars(null);
                    setFreeCancel(false);
                    setBreakfast(false);
                  }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          </aside>

          <div className="space-y-4">
            {rows.length === 0 ? (
              <div className="rounded-lg border border-[#e7e7e7] bg-white p-8 text-center">
                <p className="font-semibold">No stays match that search.</p>
                <button
                  type="button"
                  className="mt-3 text-sm font-semibold text-[#006ce4]"
                  onClick={() => setSearch({ destination: "" })}
                >
                  Show every property
                </button>
              </div>
            ) : (
              rows.map(({ hotel, quote }) => (
                <article
                  key={hotel.id}
                  className="grid overflow-hidden rounded-lg border border-[#e7e7e7] bg-white md:grid-cols-[240px_1fr]"
                >
                  <img
                    src={hotel.image}
                    alt=""
                    className="h-48 w-full object-cover md:h-full"
                  />
                  <div className="flex flex-col gap-3 p-4 sm:flex-row">
                    <div className="min-w-0 flex-1">
                      <StarRow count={hotel.stars} />
                      <h2 className="text-lg font-bold text-[#006ce4]">{hotel.name}</h2>
                      <p className="text-sm text-[#595959]">
                        {hotel.city}, {hotel.state}
                      </p>
                      <div className="mt-2">
                        <ScorePill rating={hotel.rating} reviews={hotel.reviews} />
                      </div>
                      <p className="mt-3 text-sm text-[#008009]">
                        {hotel.freeCancellation ? "Free cancellation" : "Non-refundable"}
                        {hotel.breakfastIncluded ? " · Breakfast included" : ""}
                      </p>
                      <p className="mt-1 text-sm text-[#595959]">{hotel.tagline}</p>
                    </div>
                    <div className="flex flex-col items-start justify-between sm:items-end">
                      <div className="text-left sm:text-right">
                        <p className="text-xs font-semibold text-[#008009]">
                          Member price
                        </p>
                        <p className="text-2xl font-bold">
                          {formatMoney(quote?.total ?? hotel.pricePerNight, wallet.currency)}
                        </p>
                        <p className="text-xs text-[#595959]">
                          {nights} night{nights === 1 ? "" : "s"}, {search.rooms} room
                          {search.rooms === 1 ? "" : "s"} · includes taxes
                        </p>
                      </div>
                      <Link
                        to={`/member/hotels/${hotel.id}`}
                        className="mt-3 inline-flex h-10 items-center rounded-md bg-[#006ce4] px-4 text-sm font-semibold text-white hover:bg-[#0057b8]"
                      >
                        See availability
                      </Link>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </PageWidth>
    </div>
  );
}
